import React from "react";
import PropTypes from "prop-types";
import { ArrowRight, FileText } from "react-feather";
import SubscriptionBannerIllustration from "../components/SubscriptionBannerIllustration";

const formatDate = (isoStr) => {
  if (!isoStr) return "End of period";
  try {
    const d = new Date(isoStr);
    return isNaN(d.getTime())
      ? isoStr
      : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch (e) {
    return isoStr;
  }
};

const formatCents = (cents) => {
  if (typeof cents !== "number") return null;
  return `$${Math.round(cents / 100).toLocaleString()}`;
};

export const getScheduledRenewalPlanName = (renewal, currentPlanData) => {
  const plan = currentPlanData?.data || currentPlanData || {};
  const currentPlanName =
    renewal?.plan_name ||
    plan?.subscription_name ||
    plan?.plan ||
    "Membership";

  const scheduled =
    renewal?.scheduled_subscription ||
    plan?.upcoming_subscription;

  if (scheduled) {
    return (
      scheduled.plan_name ||
      scheduled.subscription_name ||
      scheduled.name ||
      currentPlanName
    );
  }
  return currentPlanName;
};

export const getScheduledRenewalStartDate = (renewal, currentPlanData) => {
  const plan = currentPlanData?.data || currentPlanData || {};
  const scheduled =
    renewal?.scheduled_subscription ||
    plan?.upcoming_subscription;

  const rawEndDate =
    renewal?.period_end_at ||
    renewal?.renewal_date ||
    plan?.expiry_date ||
    plan?.subscription_end_date;

  if (scheduled) {
    return (
      scheduled.starts_at ||
      scheduled.period_start_at ||
      scheduled.start_date ||
      scheduled.startDate ||
      rawEndDate
    );
  }
  return rawEndDate;
};

const MembershipRenewalSummary = ({
  renewal,
  currentPlanData,
  onUpgradeClick,
  onRequestInvoice
}) => {
  // Guide Section 3.6: Only appears <= 60 days from period end; null otherwise.
  if (!renewal && !currentPlanData?.data?.upgrade_eligible) return null;

  const currentPlanName =
    renewal?.plan_name ||
    currentPlanData?.data?.subscription_name ||
    currentPlanData?.data?.plan ||
    "Membership";

  const scheduled =
    renewal?.scheduled_subscription ||
    currentPlanData?.data?.upcoming_subscription ||
    currentPlanData?.upcoming_subscription;
  const hasScheduledRenewal = Boolean(scheduled);

  const upcomingPlanName = getScheduledRenewalPlanName(renewal, currentPlanData);
  const scheduledStartDate = getScheduledRenewalStartDate(renewal, currentPlanData);

  const daysUntilRenewal =
    renewal?.days_until_renewal ??
    currentPlanData?.data?.days_until_renewal ??
    null;

  const isVisible = renewal?.visible !== false && daysUntilRenewal !== null && daysUntilRenewal <= 60;
  if (!isVisible) return null;

  const rawEndDate =
    renewal?.period_end_at ||
    renewal?.renewal_date ||
    currentPlanData?.data?.expiry_date ||
    currentPlanData?.data?.subscription_end_date;
  const renewalDate = formatDate(rawEndDate);

  // Guide Section 3.6: annual_offer uses minor units (cents). Never recalculate in browser.
  // Missing annual_offer does not show guessed savings.
  const annualOffer = renewal?.annual_offer;
  let savingsFormatted = null;
  if (annualOffer?.savings_minor) {
    const dollars = formatCents(annualOffer.savings_minor);
    savingsFormatted = annualOffer.discount_percent
      ? `${dollars}/yr (${annualOffer.discount_percent}% off)`
      : dollars;
  }

  const isInvoiceMode = renewal?.billing_mode === "invoice";

  return (
    <div className="membership-renewal-card" id="brand-membership-renewal-summary">
      <div className="renewal-card-body">
        <div className="renewal-info-group">
          <div className="renewal-icon-circle">
            <SubscriptionBannerIllustration
              type={hasScheduledRenewal ? "scheduled" : "membership"}
              size={24}
            />
          </div>
          <div className="renewal-text-content">
            <h4 className="renewal-heading">
              {hasScheduledRenewal
                ? `${upcomingPlanName} • Renewal Confirmed`
                : `${currentPlanName} • ${daysUntilRenewal} Days Until Renewal`}
            </h4>

            {hasScheduledRenewal ? (
              <p className="renewal-subtitle">
                Your paid renewal is confirmed. Your next billing period begins on{" "}
                <strong>{formatDate(scheduledStartDate)}</strong>.
              </p>
            ) : (
              <p className="renewal-subtitle">
                Your billing period ends on <strong>{renewalDate}</strong>.
                {savingsFormatted ? (
                  <>
                    {" "}Switch to an annual plan to <strong>save {savingsFormatted}</strong> and maintain continuous access to dietitian partnerships.
                  </>
                ) : (
                  <> Maintain continuous access to dietitian partnerships and outreach tools.</>
                )}
              </p>
            )}
          </div>
        </div>

        <div className="renewal-actions">
          {/* Guide Section 9: Paid scheduled renewal replaces the annual-upgrade prompt */}
          {!hasScheduledRenewal && annualOffer && onUpgradeClick && (
            <button
              type="button"
              className="btn-upgrade-annual"
              onClick={onUpgradeClick}
            >
              Upgrade & Save
              <ArrowRight size={14} className="ml-50" />
            </button>
          )}

          {!hasScheduledRenewal && isInvoiceMode && onRequestInvoice && (
            <button
              type="button"
              className="btn-request-invoice"
              onClick={onRequestInvoice}
            >
              <FileText size={14} className="mr-50" />
              Pay by Invoice
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

MembershipRenewalSummary.propTypes = {
  renewal: PropTypes.object,
  currentPlanData: PropTypes.object,
  onUpgradeClick: PropTypes.func,
  onRequestInvoice: PropTypes.func
};

export default MembershipRenewalSummary;
